import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

import type { IMoreInfoPayload } from '@modules/bonusManagement/types';

import type { Draft } from '@reduxjs/toolkit';

export interface IErrorDetails {
  code: string;
  title: string;
  translationKey?: string;
  placeholders?: Record<string, string>;
  source: {
    fieldName: string;
    fieldValue: string;
    fieldPointer: string;
  };
}

export type MaybeDrafted<T> = T | Draft<T>;

export interface ICommonError {
  status: number;
  data: {
    type: string;
    title: string;
    detail: string;
    status: number;
    error?: string;
    traceId: string;
    instance: string;
    timestamp: number;
    translationKey?: string;
    errors?: IErrorDetails[];
    placeholders?: Record<string, string>;
  };
}

export type AdditionalPropertyFilters = Record<string, Record<string, unknown>>;

export interface IBrand {
  id: number;
  title: string;
  domainName?: string;
}

export interface IMoreInfoPopupData extends IMoreInfoPayload {
  brands: string[];
  trigger?: string;
  id: number | string;
  targetGroup: string;
  segmentCondition: string;
}

export enum MODULE {
  ACTIVATE_CODE = 'activateCode',
  AUTOMATIC_CAMPAIGN = 'automaticCampaign',
  BONUS = 'bonus',
  BONUSES_PENDING_REVIEW = 'bonusesPendingReview',
  CAMPAIGN = 'campaign', // Description: Remove after Orbit impl in BS-4097
  CAMPAIGN_ACTIVITY = 'campaignActivity',
  CAMPAIGN_MONITOR = 'campaignMonitor',
  CODE_CAMPAIGN = 'codeCampaign',
  CONTRIBUTION_BETS_REPORT = 'contributionBetsReport',
  GIVE_BONUS = 'giveBonus',
  PLAYER_BONUS = 'playerBonus',
  PLAYER_CAMPAIGNS = 'player_campaigns',
  PLAYER_CODES = 'playerCodes',
  PLAYER_OFFERS = 'playerOffers',
  SCHEDULED_CAMPAIGN = 'scheduledCampaign',
}

export enum CAMPAIGN_TYPE {
  AUTOMATIC = 'AUTOMATIC',
  CODE = 'CODE',
  SCHEDULED = 'SCHEDULED',
}

export type Combinator = 'OR' | 'AND';

export enum IFRAME {
  ACTIVATE_CODE = 'ACTIVATE_CODE',
  BONUSES_PENDING_REVIEW = 'BONUSES_PENDING_REVIEW',
  CAMPAIGN_ACTIVITY = 'CAMPAIGN_ACTIVITY',
  CAMPAIGN_MONITOR = 'CAMPAIGN_MONITOR',
  GIVE_BONUS = 'GIVE_BONUS',
  PLAYER_BONUSES = 'PLAYER_BONUSES',
  PLAYER_CAMPAIGNS = 'PLAYER_CAMPAIGNS',
  PLAYER_CODES = 'PLAYER_CODES',
  PLAYER_OFFERS = 'PLAYER_OFFERS',
}

export enum POST_MESSAGE_EVENT_TYPE {
  CLOSE_ACTIVATE_CODE_MODAL = 'CLOSE_ACTIVATE_CODE_MODAL',
  CLOSE_GIVE_BONUS_MODAL = 'CLOSE_GIVE_BONUS_MODAL',
  COPY_EDIT_ID = 'copyEditId',
  CREDENTIALS_RECEIVED = 'CREDENTIALS_RECEIVED',
  ERROR_ACTIVATE_CODE_MODAL = 'ERROR_ACTIVATE_CODE_MODAL',
  ERROR_GIVE_BONUS_MODAL = 'ERROR_GIVE_BONUS_MODAL',
  OPEN_AUTOMATIC_CAMPAIGN_DETAILS = 'OPEN_AUTOMATIC_CAMPAIGN_DETAILS',
  OPEN_BONUS_TEMPLATE_DETAILS = 'OPEN_BONUS_TEMPLATE_DETAILS',
  OPEN_CODE_CAMPAIGN_DETAILS = 'OPEN_CODE_CAMPAIGN_DETAILS',
  OPEN_FREE_SPIN_DETAILS = 'OPEN_FREE_SPIN_DETAILS',
  OPEN_SCHEDULED_CAMPAIGN_DETAILS = 'OPEN_SCHEDULED_CAMPAIGN_DETAILS',
  SET_CREDENTIALS = 'SET_CREDENTIALS',
  SUCCESS_ACTIVATE_CODE_MODAL = 'SUCCESS_ACTIVATE_CODE_MODAL',
  SUCCESS_GIVE_BONUS_MODAL = 'SUCCESS_GIVE_BONUS_MODAL',
  WARNING_ACTIVATE_CODE_MODAL = 'WARNING_ACTIVATE_CODE_MODAL',
}

export enum STATUS {
  ACCEPTED = 'ACCEPTED',
  ACTIVATED = 'ACTIVATED',
  ACTIVE = 'ACTIVE',
  AVAILABLE = 'AVAILABLE',
  AWARDED = 'AWARDED',
  BONUS_AWARDED = 'BONUS_AWARDED',
  BONUS_AWARDING_FAILED = 'BONUS_AWARDING_FAILED',
  BONUS_NOT_OFFERED = 'BONUS_NOT_OFFERED',
  BONUS_OFFERED = 'BONUS_OFFERED',
  CAMPAIGN_ACTIVATED = 'CAMPAIGN_ACTIVATED',
  CAMPAIGN_ACTIVATION_FAILED = 'CAMPAIGN_ACTIVATION_FAILED',
  CANCELLED = 'CANCELLED',
  CONTRIBUTED = 'CAMPAIGN_CONTRIBUTED',
  DECLINED = 'DECLINED',
  DEFERRED_OPERATION_PENDING = 'DEFERRED_OPERATION_PENDING',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  FAILURE = 'FAILURE',
  INACTIVE = 'INACTIVE',
  NOT_OFFERED = 'NOT_OFFERED',
  OFFERED = 'OFFERED',
  PARTIAL = 'PARTIAL',
  PENDING = 'PENDING',
  PLAYER_CANCELLED = 'PLAYER_CANCELLED',
  SUCCESS = 'SUCCESS',
}

export type FormErrors = Record<
  string,
  FieldError | Merge<FieldError, FieldErrorsImpl>
>;

export interface ISectionData {
  order: number;
  tab: number | string;
  fields: string[] | 'unavailable';
}

export interface IInvalidSection extends ISectionData {
  key: string;
}

export interface ISortSectionsByOrderParameters {
  sections: IInvalidSection[];
  tabsOrder: IInvalidSectionsParameters['tabsOrder'];
}

export interface IInvalidSectionsParameters {
  formErrors: FormErrors;
  tabsOrder: Record<string, number>;
  sectionsData: Record<string, ISectionData>;
  additionalInvalidSections?: Record<string, boolean>;
}
